import { API } from '../api.js';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import ThemeSwitcher from '../components/ThemeSwitcher';

/* ── Animated canvas background ── */
function AnimatedBackground({ isLight }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    function resize() {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);
    const ro = null;

    const lightOrbs = [
      { x:0.15, y:0.4,  r:330, color:'rgba(108,99,255,0.32)',  vx:0.00015,  vy:0.00010  },
      { x:0.82, y:0.2,  r:280, color:'rgba(56,189,248,0.28)',  vx:-0.00011, vy:0.00016  },
      { x:0.5,  y:0.8,  r:300, color:'rgba(236,72,153,0.24)',  vx:0.00009,  vy:-0.00013 },
      { x:0.08, y:0.75, r:220, color:'rgba(16,185,129,0.22)',  vx:0.00018,  vy:0.00008  },
      { x:0.9,  y:0.55, r:200, color:'rgba(251,191,36,0.25)',  vx:-0.00012, vy:-0.00009 },
    ];
    const darkOrbs = [
      { x:0.15, y:0.4,  r:330, color:'rgba(108,99,255,0.28)',  vx:0.00015,  vy:0.00010  },
      { x:0.82, y:0.2,  r:280, color:'rgba(56,189,248,0.22)',  vx:-0.00011, vy:0.00016  },
      { x:0.5,  y:0.8,  r:300, color:'rgba(236,72,153,0.18)',  vx:0.00009,  vy:-0.00013 },
      { x:0.08, y:0.75, r:220, color:'rgba(16,185,129,0.17)',  vx:0.00018,  vy:0.00008  },
      { x:0.9,  y:0.55, r:200, color:'rgba(251,191,36,0.20)',  vx:-0.00012, vy:-0.00009 },
    ];
    const ORBS = isLight ? lightOrbs : darkOrbs;

    const N = 60;
    const particles = Array.from({ length: N }, () => ({
      x: Math.random(), y: Math.random(),
      r: 1.8 + Math.random() * 2.8,
      alpha: isLight ? (0.45 + Math.random() * 0.25) : (0.28 + Math.random() * 0.30),
      vx: (Math.random() - 0.5) * 0.00022,
      vy: (Math.random() - 0.5) * 0.00022,
      hue: Math.floor(Math.random() * 360),
    }));

    const LINE_DIST = 0.18;
    const shapes = [
      { cx:0.06, cy:0.15, size:32, speed:0.0007, phase:0   },
      { cx:0.93, cy:0.25, size:24, speed:0.0011, phase:1.2 },
      { cx:0.12, cy:0.82, size:20, speed:0.0009, phase:2.5 },
      { cx:0.88, cy:0.75, size:28, speed:0.0006, phase:3.8 },
    ];

    let t = 0;
    function draw() {
      const W = canvas.width, H = canvas.height;
      if (!W || !H) { rafRef.current = requestAnimationFrame(draw); return; }
      ctx.clearRect(0, 0, W, H);

      ORBS.forEach(o => {
        o.x += o.vx; o.y += o.vy;
        if (o.x < -0.1 || o.x > 1.1) o.vx *= -1;
        if (o.y < -0.1 || o.y > 1.1) o.vy *= -1;
        const g = ctx.createRadialGradient(o.x*W, o.y*H, 0, o.x*W, o.y*H, o.r);
        g.addColorStop(0, o.color); g.addColorStop(1, 'transparent');
        ctx.beginPath(); ctx.arc(o.x*W, o.y*H, o.r, 0, Math.PI*2);
        ctx.fillStyle = g; ctx.fill();
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i+1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x, dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx*dx + dy*dy);
          if (d < LINE_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x*W, particles[i].y*H);
            ctx.lineTo(particles[j].x*W, particles[j].y*H);
            ctx.strokeStyle = `rgba(130,110,255,${(1-d/LINE_DIST)*(isLight?0.32:0.22)})`;
            ctx.lineWidth = 1.1; ctx.stroke();
          }
        }
      }

      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > 1) p.vx *= -1;
        if (p.y < 0 || p.y > 1) p.vy *= -1;
        const pulse = 0.85 + 0.15*Math.sin(t*0.002 + p.hue);
        ctx.beginPath(); ctx.arc(p.x*W, p.y*H, p.r*pulse, 0, Math.PI*2);
        ctx.fillStyle = `hsla(${p.hue},70%,55%,${p.alpha*pulse})`; ctx.fill();
      });

      shapes.forEach((s, idx) => {
        const fy = Math.sin(t*s.speed + s.phase)*14;
        ctx.save(); ctx.translate(s.cx*W, s.cy*H + fy); ctx.rotate(t*s.speed*0.5 + s.phase);
        ctx.strokeStyle = `rgba(120,100,255,${(isLight?0.30:0.20)+0.08*Math.sin(t*0.001+idx)})`;
        ctx.lineWidth = 1.6;
        if (idx % 2 === 0) { const h=s.size/2; ctx.strokeRect(-h,-h,s.size,s.size); }
        else { ctx.beginPath(); ctx.moveTo(0,-s.size/2); ctx.lineTo(s.size/2,s.size/2); ctx.lineTo(-s.size/2,s.size/2); ctx.closePath(); ctx.stroke(); }
        ctx.restore();
      });

      t++; rafRef.current = requestAnimationFrame(draw);
    }
    rafRef.current = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(rafRef.current); window.removeEventListener('resize', resize); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLight]);

  return (
    <canvas ref={canvasRef}
      style={{ position:'fixed', top:0, left:0, width:'100%', height:'100%', display:'block', pointerEvents:'none', zIndex:0 }}
    />
  );
}

const CATEGORIES = [
  { key: 'All',             emoji: '🌐' },
  { key: 'Money',           emoji: '💰' },
  { key: 'Tech',            emoji: '💻' },
  { key: 'Health',          emoji: '🥗' },
  { key: 'Self-improvement',emoji: '📈' },
  { key: 'Hobbies',         emoji: '🎸' },
  { key: 'Music',           emoji: '🎵' },
  { key: 'Sports',          emoji: '⚽' },
  { key: 'Spirituality',    emoji: '🧘' },
  { key: 'Education',       emoji: '📚' },
  { key: 'Business',        emoji: '💼' },
  { key: 'Art',             emoji: '🎨' },
  { key: 'Gaming',          emoji: '🎮' },
  { key: 'General',         emoji: '💬' },
];

function CommunityCard({ c, onClick }) {
  const [hov, setHov] = useState(false);
  const isPaid = c.pricing === 'paid' && c.price > 0;

  return (
    <div
      onClick={() => onClick(c.slug || c.id)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: 'var(--surface)',
        backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid var(--border)',
        borderRadius: 16, overflow: 'hidden', cursor: 'pointer',
        transition: 'transform 0.18s, box-shadow 0.18s',
        transform: hov ? 'translateY(-4px)' : 'none',
        boxShadow: hov ? '0 16px 40px rgba(0,0,0,0.18)' : '0 2px 12px rgba(0,0,0,0.06)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Banner */}
      <div style={{
        height: 130,
        backgroundColor: c.color || '#0a1628',
        backgroundImage: c.coverImageUrl
          ? `url(${API}${c.coverImageUrl})`
          : `linear-gradient(135deg, ${c.color || '#0a1628'}, ${c.color || '#0a1628'}88)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {!c.coverImageUrl && <span style={{ fontSize: 54, opacity: 0.25 }}>{c.emoji || '🎓'}</span>}
        {(c.isPrivate || c.is_private === 1) && (
          <div style={{
            position: 'absolute', top: 10, right: 10,
            background: 'rgba(0,0,0,0.55)', color: '#fff',
            fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 10,
          }}>
            🔒 Private
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: '18px', flex: 1, display: 'flex', flexDirection: 'column' }}>

        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4, color: 'var(--text)', lineHeight: 1.2 }}>
          {c.name}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
          by {c.owner_name}
        </div>
        <div style={{
          fontSize: 13, color: 'var(--text2)', lineHeight: 1.5,
          flex: 1, marginBottom: 14,
          display: '-webkit-box', WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {c.description || 'No description yet.'}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)' }}>
            👥 {(c.member_count ?? 0).toLocaleString()}
          </span>
          <span style={{ color: 'var(--border2)' }}>·</span>
          <span style={{
            fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 10,
            color: isPaid ? 'var(--accent)' : 'var(--accent3)',
            background: isPaid ? 'rgba(99,91,255,0.1)' : 'rgba(16,185,129,0.1)',
          }}>
            {isPaid ? `$${c.price}/mo` : 'Free'}
          </span>
          {c.category && (
            <>
              <span style={{ color: 'var(--border2)' }}>·</span>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>{c.category}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DiscoveryPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const [communities,      setCommunities]      = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [activeCategory,   setActiveCategory]   = useState('All');
  const [search,           setSearch]           = useState('');

  useEffect(() => {
    fetch(`${API}/api/communities`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setCommunities(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = communities.filter(c => {
    const matchSearch = !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.description || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.owner_name || '').toLowerCase().includes(search.toLowerCase());
    const matchCat = activeCategory === 'All' || c.category === activeCategory;
    return matchSearch && matchCat;
  });

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: 'inherit', position: 'relative', zIndex: 1 }}>

      {/* Full-page animated canvas background */}
      <AnimatedBackground isLight={isLight} />

      {/* Full-page gradient overlay for readability */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: isLight
          ? 'rgba(245,245,255,0.32)'
          : 'rgba(10,10,18,0.58)',
      }} />

      {/* ── Navbar ── */}
      <nav style={{
        background: isLight ? 'rgba(255,255,255,0.72)' : 'rgba(15,15,22,0.72)',
        backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        borderBottom: '1px solid var(--border)',
        position: 'sticky', top: 0, zIndex: 100,
        paddingTop: 'var(--sat)',
        paddingLeft: 'var(--sal)',
        paddingRight: 'var(--sar)'
      }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', gap: 16 }}>
          <div onClick={() => navigate('/')}
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 900, fontSize: 19, letterSpacing: -0.5, color: 'var(--text)', flexShrink: 0 }}>
            <div style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <img src="/logo2.png" alt="Qeduvo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            Qeduvo
            <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--text3)', marginLeft: 1 }}>▾</span>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <ThemeSwitcher />
            <button className="hide-on-mobile" onClick={() => navigate('/create')}
              style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              + Create
            </button>
            {!authLoading && (user ? (
              <button onClick={() => navigate('/app')}
                style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                My Community
              </button>
            ) : (
              <>
                <button onClick={() => navigate('/login')}
                  style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  Log in
                </button>
                <button onClick={() => navigate('/signup')}
                  style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                  Get started
                </button>
              </>
            ))}
          </div>
        </div>
      </nav>

      {/* ── Hero search ── */}
      <div style={{ padding:'56px 24px 42px', textAlign:'center', position:'relative', zIndex:2 }}>
        <h1 style={{ fontSize:'clamp(26px,5vw,42px)', fontWeight:900, marginBottom:8, letterSpacing:-1, color:'var(--text)' }}>
          Discover communities
        </h1>
        <p style={{ fontSize:15, color:'var(--text3)', marginBottom:28 }}>
          or{' '}
          <span onClick={() => navigate('/create')}
            style={{ color:'var(--accent)', cursor:'pointer', textDecoration:'underline', fontWeight:600 }}>
            create your own
          </span>
        </p>

        {/* Search bar */}
        <div style={{
          maxWidth:580, margin:'0 auto',
          background: isLight ? 'rgba(255,255,255,0.85)' : 'rgba(30,30,45,0.85)',
          backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)',
          border:'1px solid var(--border)',
          borderRadius:40, padding:'13px 22px',
          display:'flex', alignItems:'center', gap:12,
          boxShadow:'0 4px 24px rgba(0,0,0,0.1)',
        }}>
          <span style={{ fontSize:20, color:'var(--text3)', flexShrink:0 }}>🔍</span>
          <input
            type="text" placeholder="Search communities…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex:1, border:'none', background:'transparent', outline:'none', fontSize:16, color:'var(--text)' }}
          />
          {search && (
            <button onClick={() => setSearch('')}
              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:20, lineHeight:1, flexShrink:0 }}>
              ×
            </button>
          )}
        </div>
      </div>

      {/* ── Category pills ── */}
      <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 24px 28px', display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', position:'relative', zIndex:2 }}>
        {CATEGORIES.map(cat => (
          <button key={cat.key} onClick={() => setActiveCategory(cat.key)}
            style={{
              padding: '8px 18px', borderRadius: 30, border: '1px solid var(--border)',
              cursor: 'pointer', fontSize: 14, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 7, transition: 'all 0.15s',
              background: activeCategory === cat.key ? 'var(--text)' : 'var(--surface)',
              color: activeCategory === cat.key ? 'var(--bg)' : 'var(--text2)',
            }}>
            <span>{cat.emoji}</span>
            <span>{cat.key}</span>
          </button>
        ))}
      </div>

      {/* ── Community grid ── */}
      <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 24px 80px', position:'relative', zIndex:2 }}>

        {/* Loading */}
        {loading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(310px,1fr))', gap: 22 }}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
                <div style={{ height: 130, background: 'var(--surface2)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                <div style={{ padding: '32px 18px 18px' }}>
                  <div style={{ width: '60%', height: 16, background: 'var(--surface2)', borderRadius: 6, marginBottom: 10 }} />
                  <div style={{ width: '40%', height: 12, background: 'var(--surface2)', borderRadius: 6, marginBottom: 14 }} />
                  <div style={{ width: '100%', height: 10, background: 'var(--surface2)', borderRadius: 6, marginBottom: 6 }} />
                  <div style={{ width: '80%', height: 10, background: 'var(--surface2)', borderRadius: 6 }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state — no communities at all */}
        {!loading && communities.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 24px' }}>
            <div style={{ fontSize: 64, marginBottom: 20 }}>🏘️</div>
            <h2 style={{ fontSize: 24, fontWeight: 900, marginBottom: 10, color: 'var(--text)' }}>
              No communities yet
            </h2>
            <p style={{ fontSize: 15, color: 'var(--text3)', marginBottom: 32, maxWidth: 400, margin: '0 auto 32px' }}>
              Be the first to create a community on Qeduvo and bring your people together.
            </p>
            <button onClick={() => navigate('/create')}
              style={{ padding: '14px 36px', borderRadius: 12, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 16, fontWeight: 800, boxShadow: '0 8px 24px rgba(99,91,255,0.35)' }}>
              Create the first community →
            </button>
          </div>
        )}

        {/* Empty state — search/filter has no results */}
        {!loading && communities.length > 0 && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 24px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: 'var(--text)' }}>
              No communities match "{search || activeCategory}"
            </h2>
            <p style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 24 }}>
              Try a different search or category — or create one!
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => { setSearch(''); setActiveCategory('All'); }}
                style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                Clear filters
              </button>
              <button onClick={() => navigate('/create')}
                style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                + Create community
              </button>
            </div>
          </div>
        )}

        {/* Real community grid */}
        {!loading && filtered.length > 0 && (
          <>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20, fontWeight: 600 }}>
              {filtered.length} {filtered.length === 1 ? 'community' : 'communities'}
              {activeCategory !== 'All' && ` in ${activeCategory}`}
              {search && ` matching "${search}"`}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(310px,1fr))', gap: 22 }}>
              {filtered.map(c => (
                <CommunityCard key={c.id} c={c} onClick={id => navigate(`/c/${id}`)} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
